'use client';

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Service, isDayAvailable } from '@/lib/bookingStore';

interface Props {
  selectedServices: Service[];
  selectedDate: string;
  onSelect: (date: string) => void;
  onNext: () => void;
  onBack: () => void;
}

const MONTHS = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const DAYS = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

export default function AgendarStepCalendar({ selectedServices, selectedDate, onSelect, onNext, onBack }: Props) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const isBeforeToday = (day: number) => {
    const d = new Date(viewYear, viewMonth, day);
    const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return d < t;
  };

  const getDateStr = (day: number) => {
    const m = String(viewMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${viewYear}-${m}-${d}`;
  };

  const isAvailable = (day: number) => {
    if (isBeforeToday(day)) return false;
    return isDayAvailable(getDateStr(day));
  };

  const totalDuration = selectedServices.reduce((sum, s) => sum + s.duration, 0);

  return (
    <div className="space-y-8">
      <div>
        <span className="gold-line mb-4 block" />
        <h2 className="font-serif text-3xl lg:text-4xl font-light text-dark-brown mb-2">
          Escolha a data
        </h2>
        <p className="text-sm text-foreground/60">
          Duração total da sessão: <span className="font-semibold text-burnt-rose">{totalDuration} min</span>
        </p>
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        {/* Calendar header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <button
            onClick={prevMonth}
            className="p-2 rounded-full hover:bg-muted transition-colors text-foreground/60 hover:text-foreground"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="font-serif text-lg font-medium text-dark-brown">
            {MONTHS[viewMonth]} {viewYear}
          </span>
          <button
            onClick={nextMonth}
            className="p-2 rounded-full hover:bg-muted transition-colors text-foreground/60 hover:text-foreground"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Day names */}
        <div className="grid grid-cols-7 px-4 pt-3">
          {DAYS.map(d => (
            <div key={d} className="text-center text-[10px] font-semibold tracking-wide text-foreground/40 uppercase py-2">
              {d}
            </div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7 px-4 pb-4 gap-1">
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateStr = getDateStr(day);
            const available = isAvailable(day);
            const isSelected = selectedDate === dateStr;
            const isPast = isBeforeToday(day);
            const isToday = dateStr === `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

            return (
              <button
                key={day}
                disabled={!available}
                onClick={() => available && onSelect(dateStr)}
                className={`aspect-square flex items-center justify-center rounded-full text-sm font-medium transition-all duration-200 ${
                  isSelected
                    ? 'bg-burnt-rose text-white font-bold shadow-md'
                    : available
                    ? `hover:bg-blush hover:text-burnt-rose cursor-pointer ${isToday ? 'border-2 border-burnt-rose text-burnt-rose font-bold' : 'text-foreground'}`
                    : 'text-foreground/25 cursor-not-allowed'
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-5 px-6 pb-4 text-xs text-foreground/50">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-burnt-rose" />
            <span>Selecionado</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full border-2 border-burnt-rose" />
            <span>Hoje</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-foreground/15" />
            <span>Indisponível</span>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-4 rounded-full border-2 border-border text-sm font-semibold tracking-wide uppercase text-foreground/60 hover:border-burnt-rose hover:text-burnt-rose transition-all duration-200"
        >
          Voltar
        </button>
        <button
          onClick={onNext}
          disabled={!selectedDate}
          className={`flex-2 flex-grow-[2] py-4 rounded-full font-semibold text-sm tracking-[0.1em] uppercase transition-all duration-300 ${
            selectedDate ? 'btn-primary' : 'bg-muted text-foreground/40 cursor-not-allowed'
          }`}
        >
          Ver Horários
        </button>
      </div>
    </div>
  );
}
